import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  Grid,
  Alert,
  Box,
  Chip,
  CircularProgress
} from '@mui/material';
import { 
  Save as SaveIcon, 
  Lock as LockIcon,
  Refresh as RefreshIcon 
} from '@mui/icons-material';
import { useSettings } from '../../contexts/SettingsContext';
import { apiClient } from '../../services/api';

interface BackupSettingsProps {
  salonId: string;
}

const BackupSettings: React.FC<BackupSettingsProps> = ({ salonId }) => {
  const [settings, setSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const { checkAccess, getRestrictions } = useSettings();
  
  const canWrite = checkAccess('backup', 'write');
  const restrictions = getRestrictions('backup');

  useEffect(() => {
    fetchSettings();
  }, [salonId]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/settings/${salonId}/backup`);
      
      if (response.success) {
        setSettings(response.data.settings);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load backup settings');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pr: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Backup & Recovery Settings</Typography>
        <Chip label="Super Admin Only" color="primary" size="small" />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Automatic Backup
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={settings?.automaticBackup?.enabled || false}
                    disabled={!canWrite}
                  />
                }
                label="Enable Automatic Backup"
              />
              
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Frequency: {settings?.automaticBackup?.frequency || 'daily'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BackupSettings;